/**
 * 「几倍单掷金币」（×g）与 B 个数的换算（leo 2026-10-22）。
 *
 * 单掷金币 g = E × B（E = 每掷平均产出几个 B，cfg_main_common.e_target_milli_b ÷ 1000）。
 * 运营看不懂 B（E = 600 时「10 B」只是 0.017 掷的钱），所以副玩法页上按 B 存的数
 * 界面一律按 ×g 填。**存储不变**：库里仍是 B 的个数（整数列），只在界面换算：
 *   显示 = B ÷ E（最多 3 位有效数字）；保存 B = round(输入 × E)，最小粒度 1 个 B。
 * E 没配、为 0 或不是数时退回按 B 原样显示和输入。
 *
 * 纯函数，不依赖 React：输入框组件见 components/data/GNumInput.tsx。
 */

/** E 能不能拿来换算；不能时返回 null（调用方退回按 B） */
export function usableE(e: number | null | undefined): number | null {
  return e != null && Number.isFinite(e) && e > 0 ? e : null;
}

/** 最多 3 位有效数字，去掉多余的 0（0.0167、0.5、10、1230） */
export function fmt3(x: number): number {
  if (!Number.isFinite(x)) return x;
  if (x === 0) return 0;
  return Number(x.toPrecision(3));
}

/** B 个数 → 界面上的 ×g 值。E 不可用时原样返回 B */
export function bToG(b: number | null | undefined, e: number | null | undefined): number | null {
  if (b == null || !Number.isFinite(b)) return null;
  const E = usableE(e);
  return E == null ? b : fmt3(b / E);
}

/** 界面上的 ×g 值 → 要存的 B 个数（四舍五入到整数个 B）。E 不可用时原样返回输入 */
export function gToB(x: number | null | undefined, e: number | null | undefined): number | null {
  if (x == null || !Number.isFinite(x)) return null;
  const E = usableE(e);
  if (E == null) return x;
  const b = Math.round(x * E);
  return Object.is(b, -0) ? 0 : b;
}

/**
 * 输入的倍数存不成原样时（取整到 1 个 B 后显示出来不一样），返回实际保存的倍数；
 * 能原样表示（或 E 不可用、没填）时返回 null。
 * 按 3 位有效数字比：把显示出来的 0.0167 原样再填一遍不算「改了值」。
 * 例：E = 3.1 填 1 → 存 3 B → 实际 0.968 倍。
 */
export function gSavedAs(x: number | null | undefined, e: number | null | undefined): number | null {
  if (x == null || !Number.isFinite(x) || usableE(e) == null) return null;
  const saved = bToG(gToB(x, e), e);
  return saved == null || saved === fmt3(x) ? null : saved;
}
